// Payroll periods — locking a month, and the amendment that is the only way back
// into one.
//
// The database is the boundary (20260829100000_period_lock.sql). Nothing here
// decides anything: `lockPeriod` is refused by a trigger if the month has not
// ended, `amendPeriod` is refused if the month is not locked, and the amendment
// window is derived server-side from how long ago the exception was filed. What
// this module does is name those operations and shape their rows.
//
// Kept out of the screen deliberately: the colocated test mocks this module
// rather than a chainable PostgREST builder (see components/timer.test.tsx).
import { supabase } from './supabase';
import { write } from './write';

/**
 * Mirrors `interval '2 hours'` in assert_period_open. Display only — the window
 * a write actually gets is whatever the database says, and this number drifting
 * would cost a countdown that reads slightly wrong, never a wrong permission.
 */
export const AMEND_WINDOW_MS = 2 * 60 * 60 * 1000;

export type PeriodStatus = 'open' | 'locked' | 'paid' | 'amended';

export type Period = {
  year: number;
  month: number;
  status: PeriodStatus;
  locked_at: string | null;
  locked_by: string | null;
  amended_at: string | null;
};

/** An amendment on file. `created_at` is what opens and closes the window. */
export type Amendment = { subject_id: string; reason: string; created_at: string };

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "August 2026" — a payroll month's name, in fixed English (UI-CONVENTIONS §2). The
 *  register, the periods card and the amendment workspace each kept their own copy. */
export const monthLabel = (year: number, month: number): string => `${MONTHS[month - 1]} ${year}`;

/** "14:30" on the viewer's own clock: an amendment window's deadline is read
 *  against the clock in front of them, not the org's. */
export const clockTime = (d: Date): string =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/** 'YYYY-MM' — the key the database files a period exception against. */
export const periodKey = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, '0')}`;

/** The last `n` months, newest first, including the one in progress. */
export const recentMonths = (
  n: number,
  today: Date = new Date(),
): { year: number; month: number }[] =>
  Array.from({ length: n }, (_, i) => {
    // Build from explicit fields rather than setMonth(), which overflows: on the
    // 31st, setMonth(m - 1) lands in the month after the one you asked for.
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });

/** True once the month is over — the database refuses to lock it before then. */
export const monthHasEnded = (year: number, month: number, today: Date = new Date()): boolean =>
  new Date(year, month, 1) <= new Date(today.getFullYear(), today.getMonth(), 1);

/**
 * When the amendment window on `key` closes, or null if no window is open.
 * Derived from the newest exception's age, exactly as assert_period_open does.
 */
export const windowOpenUntil = (
  key: string,
  amendments: readonly Amendment[],
  now: number = Date.now(),
): Date | null => {
  const newest = amendments
    .filter((a) => a.subject_id === key)
    .map((a) => new Date(a.created_at).getTime())
    .sort((a, b) => b - a)[0];
  if (newest === undefined) return null;
  const closes = newest + AMEND_WINDOW_MS;
  return closes > now ? new Date(closes) : null;
};

/** A read's rows, or why there are none. A failed read is not an empty list
 *  (UI-CONVENTIONS §7): the caller renders LoadError on `error`, never "nothing yet". */
export type Read<T> = { data: T; error: { message: string } | null };

export async function listPeriods(): Promise<Read<Period[]>> {
  if (!supabase) return { data: [], error: null };
  const { data, error } = await supabase
    .from('payroll_period')
    .select('year, month, status, locked_at, locked_by, amended_at')
    .order('year', { ascending: false })
    .order('month', { ascending: false })
    .limit(24);
  return { data: (data as Period[]) ?? [], error };
}

export async function listAmendments(): Promise<Read<Amendment[]>> {
  if (!supabase) return { data: [], error: null };
  const { data, error } = await supabase
    .from('hr_exception')
    .select('subject_id, reason, created_at')
    .eq('subject_type', 'payroll_period')
    .order('created_at', { ascending: false })
    .limit(50);
  return { data: (data as Amendment[]) ?? [], error };
}

type Result = { error: { message: string } | null };

/**
 * Upsert rather than insert: a month with no row is open, and one already marked
 * open needs the same write. Anything further along hits the trigger's own
 * refusal, which already says what to do instead.
 */
export async function lockPeriod(year: number, month: number): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  // Through write(): the table keys on (year, month), and a write RLS filters
  // out comes back as zero rows with no error — a lock that did not happen.
  const r = await write(
    supabase
      .from('payroll_period')
      .upsert({ year, month, status: 'locked' }, { onConflict: 'year,month' }),
    'year',
  );
  if (r.ok) return { error: null };
  return { error: r.reason === 'refused' ? { message: 'You can’t lock this month.' } : r.error };
}

/**
 * Filing the exception IS opening the window — there is no separate unlock call.
 * `approved_by` is deliberately absent from the payload: the trigger stamps it
 * from the JWT, so a browser cannot name someone else as the approver.
 */
export async function amendPeriod(year: number, month: number, reason: string): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.from('hr_exception').insert({
    subject_type: 'payroll_period',
    subject_id: periodKey(year, month),
    action: 'amend locked period',
    standard_value: 'locked',
    actual_value: 'amended',
    reason: reason.trim(),
  });
  return { error };
}
